import React, { useEffect, useState } from "react";
import { api, formatMoney, formatTRY } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useViewCurrency } from "@/context/ViewCurrencyContext";
import { Button } from "@/components/ui/button";
import { Wallet, ArrowDown, ArrowUp, Warning, Clock, Buildings, Users, CalendarX, ShieldWarning, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

function StatCard({ label, value, hint, icon: Icon, tone = "neutral", testid, children }) {
  const toneMap = {
    neutral: "text-slate-900",
    success: "text-emerald-600",
    danger: "text-red-600",
    warning: "text-amber-600",
  };
  return (
    <div className="stat-card bg-white border border-slate-200 rounded-lg p-6" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="label-eyebrow">{label}</div>
        {Icon && <Icon size={18} weight="duotone" className="text-slate-400" />}
      </div>
      {children ? (
        <div className={`mt-3 ${toneMap[tone]}`}>{children}</div>
      ) : (
        <div className={`font-heading text-3xl sm:text-4xl font-semibold mt-3 tracking-tight ${toneMap[tone]}`}>{value}</div>
      )}
      {hint && <div className="text-xs text-slate-500 mt-2">{hint}</div>}
    </div>
  );
}

function CurrencyStack({ obj, tone = "" }) {
  const keys = Object.keys(obj || {});
  if (keys.length === 0) return <div className="font-heading text-3xl font-semibold">—</div>;
  return (
    <div className="space-y-1">
      {keys.map((k) => (
        <div key={k} className={`font-heading text-2xl font-semibold ${tone}`} data-testid={`cur-${k}`}>{formatMoney(obj[k], k)}</div>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const { t, lang } = useI18n();
  const { viewCurrency } = useViewCurrency();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/dashboard?view_currency=${viewCurrency}`).then((r) => setData(r.data));
  }, [viewCurrency]);

  const runOverdue = async () => {
    try {
      const { data: r } = await api.post("/notifications/run-overdue-check");
      toast.success(`${t("overdue_ran")} (${r.notifications_total || 0})`);
    } catch (e) { toast.error("Error"); }
  };

  if (!data) return <div className="text-slate-500">…</div>;

  const monthLabel = `${t("months")[data.month - 1]} ${data.year}`;
  const vc = data.view_currency || "TRY";

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">{t("dashboard")}</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 mt-1">{monthLabel}</h1>
          <div className="text-sm text-slate-500 mt-2">{t("tagline")}</div>
        </div>
        <Button data-testid="run-overdue-btn" variant="outline" onClick={runOverdue}>
          <EnvelopeSimple size={14} className="mr-2" /> {t("run_overdue")}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard testid="stat-expected" label={t("expected_month")} icon={Wallet}>
          <CurrencyStack obj={data.expected_by_currency} />
        </StatCard>
        <StatCard testid="stat-collected" label={t("collected")} icon={ArrowDown} tone="success">
          <CurrencyStack obj={data.collected_by_currency} tone="text-emerald-600" />
        </StatCard>
        <StatCard testid="stat-overdue" label={t("overdue")} icon={Warning} tone="danger" hint={`${data.overdue_count} ${lang === "tr" ? "kayıt" : "records"}`}>
          <CurrencyStack obj={data.overdue_by_currency} tone="text-red-600" />
        </StatCard>
        <StatCard testid="stat-net" label={`${t("net")} (${vc})`} value={formatMoney(data.view_net, vc)} tone={data.view_net >= 0 ? "success" : "danger"} icon={ArrowUp} hint={t("view_currency_hint")} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatCard testid="stat-income" label={t("total_income")} icon={ArrowDown} tone="success">
          <CurrencyStack obj={data.income_by_currency} tone="text-emerald-600" />
        </StatCard>
        <StatCard testid="stat-expense" label={t("total_expense")} icon={ArrowUp} tone="danger">
          <CurrencyStack obj={data.expense_by_currency} tone="text-red-600" />
        </StatCard>
        <StatCard testid="stat-tenants" label={t("tenants_count")} value={data.tenants_count} icon={Users} />
        <StatCard testid="stat-properties" label={t("properties_count")} value={data.properties_count} icon={Buildings} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className="bg-white border border-slate-200 rounded-lg p-6" data-testid="upcoming-7d">
          <div className="flex items-center gap-2">
            <Clock size={18} weight="duotone" className="text-slate-500" />
            <h2 className="font-heading text-xl font-medium text-slate-900">{t("upcoming7")}</h2>
          </div>
          <div className="mt-4 divide-y divide-slate-100">
            {data.upcoming_7d.length === 0 && <div className="text-sm text-slate-500 py-4">{t("no_data")}</div>}
            {data.upcoming_7d.map((e) => (
              <div key={`${e.tenant_id}-${e.year}-${e.month}`} className="py-3 flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-slate-900">{e.tenant_name}</div>
                  <div className="text-xs text-slate-500">{e.due_date}</div>
                </div>
                <div className="text-sm font-semibold text-slate-900">{formatMoney(e.amount, e.currency)}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-white border border-slate-200 rounded-lg p-6" data-testid="overdue-list">
          <div className="flex items-center gap-2">
            <Warning size={18} weight="duotone" className="text-red-500" />
            <h2 className="font-heading text-xl font-medium text-slate-900">{t("overdue")}</h2>
          </div>
          <div className="mt-4 divide-y divide-slate-100">
            {data.overdue.length === 0 && <div className="text-sm text-slate-500 py-4">{t("no_data")}</div>}
            {data.overdue.slice(0, 8).map((e) => (
              <div key={`${e.tenant_id}-${e.year}-${e.month}`} className="py-3 flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-slate-900">{e.tenant_name}</div>
                  <div className="text-xs text-red-600">{e.due_date} — {t("months")[e.month - 1]} {e.year}</div>
                </div>
                <div className="text-sm font-semibold text-red-600">{formatMoney(e.amount, e.currency)}</div>
              </div>
            ))}
          </div>
        </section>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className="bg-white border border-slate-200 rounded-lg p-6" data-testid="contracts-ending">
          <div className="flex items-center gap-2">
            <CalendarX size={18} weight="duotone" className="text-amber-500" />
            <h2 className="font-heading text-xl font-medium text-slate-900">{t("contracts_ending")}</h2>
          </div>
          <div className="mt-4 divide-y divide-slate-100">
            {data.contracts_ending_soon.length === 0 && <div className="text-sm text-slate-500 py-4">{t("no_data")}</div>}
            {data.contracts_ending_soon.map((c) => (
              <div key={c.tenant_id} className="py-3 flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-slate-900">{c.tenant_name}</div>
                  <div className="text-xs text-slate-500">{c.lease_end}</div>
                </div>
                <div className="text-xs font-semibold text-amber-600 uppercase tracking-widest">
                  {c.days_left} {t("days_left")}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-white border border-slate-200 rounded-lg p-6" data-testid="insurance-expiring">
          <div className="flex items-center gap-2">
            <ShieldWarning size={18} weight="duotone" className="text-amber-500" />
            <h2 className="font-heading text-xl font-medium text-slate-900">{t("insurance_expiring")}</h2>
          </div>
          <div className="mt-4 divide-y divide-slate-100">
            {(data.insurance_expiring || []).length === 0 && <div className="text-sm text-slate-500 py-4">{t("no_data")}</div>}
            {(data.insurance_expiring || []).map((c) => {
              const isOverdue = (c.days_left ?? 0) < 0;
              return (
                <div key={c.expense_id} className="py-3 flex items-center justify-between" data-testid={`ins-${c.expense_id}`}>
                  <div>
                    <div className="text-sm font-medium text-slate-900">{c.property_name || t("property")}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {c.vendor || t("insurance")} <span className="text-slate-300 mx-1">•</span> {c.insurance_end}
                    </div>
                  </div>
                  <div className={`text-xs font-semibold uppercase tracking-widest text-right ${isOverdue ? "text-red-600" : "text-amber-600"}`}>
                    {isOverdue ? (
                      <>
                        <div>{t("insurance_overdue")}</div>
                        <div className="text-[10px] font-normal mt-0.5">{Math.abs(c.days_left)} {t("days_ago")}</div>
                      </>
                    ) : (
                      <>{c.days_left} {t("days_left")}</>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}
